import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarIcon, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { pt, es, enUS } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupRequest() {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [date, setDate] = useState();
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    trip_type: '',
    origin: '',
    destination: '',
    duration_days: '',
    num_people: '',
    observations: '',
    agency_name: '',
    first_name: '',
    last_name: '',
    position: '',
    address: '',
    postal_code: '',
    city: '',
    email: '',
    phone: '',
    mobile: ''
  });

  const locales = { pt, es, en: enUS };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyAccepted) {
      setSubmitStatus({ type: 'error', message: 'Deve aceitar a política de privacidade' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        ...formData,
        duration_days: parseInt(formData.duration_days) || 1,
        num_people: parseInt(formData.num_people) || 1,
        travel_date: date ? format(date, 'yyyy-MM-dd') : '',
        privacy_accepted: privacyAccepted
      };

      await axios.post(`${API_URL}/api/group-request`, payload);
      
      setSubmitStatus({ type: 'success', message: t('groupRequest.success') });
      
      // Reset form
      setFormData({
        trip_type: '',
        origin: '',
        destination: '',
        duration_days: '',
        num_people: '',
        observations: '',
        agency_name: '',
        first_name: '',
        last_name: '',
        position: '',
        address: '',
        postal_code: '',
        city: '',
        email: '',
        phone: '',
        mobile: ''
      });
      setDate(undefined);
      setPrivacyAccepted(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ type: 'error', message: t('groupRequest.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tripTypes = [
    { value: 'Estudantes', label: t('groupRequest.tripTypes.students') },
    { value: 'Empresa', label: t('groupRequest.tripTypes.corporate') },
    { value: 'Desportivo', label: t('groupRequest.tripTypes.sports') },
    { value: 'Congresso', label: t('groupRequest.tripTypes.congress') },
    { value: 'Outros', label: t('groupRequest.tripTypes.other') }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="group-request-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
              7x7 Travel
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('groupRequest.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('groupRequest.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="group-request-form">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Trip Type Section */}
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="w-8 h-8 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">1</span>
                  {t('groupRequest.tripSection')}
                </h2>
                <Select
                  value={formData.trip_type}
                  onValueChange={(value) => handleSelectChange('trip_type', value)}
                >
                  <SelectTrigger className="w-full h-12" data-testid="trip-type-select">
                    <SelectValue placeholder={t('groupRequest.tripSection')} />
                  </SelectTrigger>
                  <SelectContent>
                    {tripTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Travel Details Section */}
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="w-8 h-8 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">2</span>
                  {t('groupRequest.travelSection')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="origin">{t('groupRequest.origin')} *</Label>
                    <Input
                      id="origin"
                      name="origin"
                      value={formData.origin}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="origin-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="destination">{t('groupRequest.destination')} *</Label>
                    <Input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="destination-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration_days">{t('groupRequest.duration')} *</Label>
                    <Input
                      id="duration_days"
                      name="duration_days"
                      type="number"
                      min="1"
                      value={formData.duration_days}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="duration-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="num_people">{t('groupRequest.people')} *</Label>
                    <Input
                      id="num_people"
                      name="num_people"
                      type="number"
                      min="1"
                      value={formData.num_people}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="people-input"
                    />
                  </div>
                  <div>
                    <Label>{t('groupRequest.date')} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-2 h-12 justify-start text-left font-normal"
                          data-testid="date-picker-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, 'PPP', { locale: locales[language] || locales.en }) : <span className="text-slate-500">Selecione uma data</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                          locale={locales[language] || locales.en}
                          data-testid="calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="mt-6">
                  <Label htmlFor="observations">{t('groupRequest.observations')}</Label>
                  <Textarea
                    id="observations"
                    name="observations"
                    value={formData.observations}
                    onChange={handleInputChange}
                    className="mt-2 min-h-[100px]"
                    data-testid="observations-input"
                  />
                </div>
              </div>

              {/* Contact Details Section */}
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <span className="w-8 h-8 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-bold">3</span>
                  {t('groupRequest.contactSection')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="agency_name">{t('groupRequest.agency')} *</Label>
                    <Input
                      id="agency_name"
                      name="agency_name"
                      value={formData.agency_name}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="agency-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="first_name">{t('groupRequest.firstName')} *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="firstname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">{t('groupRequest.lastName')} *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="lastname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="position">{t('groupRequest.position')}</Label>
                    <Input
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="position-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t('groupRequest.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2 h-12"
                      data-testid="email-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">{t('groupRequest.address')}</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">{t('groupRequest.postalCode')}</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="postalcode-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">{t('groupRequest.city')}</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('groupRequest.phone')}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile">{t('groupRequest.mobile')}</Label>
                    <Input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="mt-2 h-12"
                      data-testid="mobile-input"
                    />
                  </div>
                </div>
              </div>

              {/* Privacy Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  className="mt-1"
                  data-testid="privacy-checkbox"
                />
                <Label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer">
                  {t('groupRequest.privacy')} *
                </Label>
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-xl flex items-center gap-3 ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                  data-testid="submit-status"
                >
                  {submitStatus.type === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  {submitStatus.message}
                </motion.div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full py-6 text-lg font-semibold"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('groupRequest.submitting')}
                  </>
                ) : (
                  t('groupRequest.submit')
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
